// MyVouchers.js
import React, { useEffect, useMemo, useState } from 'react';
import {
  SafeAreaView,
  StyleSheet,
  View,
  Text,
  ScrollView,
  Image,
  TouchableOpacity,
  ActivityIndicator,
  RefreshControl,
  Alert,
  Linking,
} from 'react-native';
import { Ionicons } from '@expo/vector-icons';

const API_URL = 'https://collabbay.com/APIControllers/FetchCreatorVouchers.php';
const SESSION_URL = 'https://collabbay.com/APIControllers/GetSessionData.php';

const LOGO_URL = 'https://collabbay.com/CollabbayLogoDark.png';
const VOUCHER_BASE_URL = 'https://collabbay.com/Voucher-';

const FALLBACK_IMAGE =
  'https://images.pexels.com/photos/1058277/pexels-photo-1058277.jpeg';

const CREATOR_EARNINGS_PER_STOCK = 5;

const STOCK_SORT_OPTIONS = [
  { key: 'latest', label: 'Latest' },
  { key: 'stock_high', label: 'Most stock' },
  { key: 'potential_high', label: 'Highest potential' },
  { key: 'sales_high', label: 'Most purchases' },
  { key: 'brand_az', label: 'Brand A-Z' },
];

const SALES_SORT_OPTIONS = [
  { key: 'latest', label: 'Newest' },
  { key: 'oldest', label: 'Oldest' },
  { key: 'amount_high', label: 'Highest amount' },
  { key: 'brand_az', label: 'Brand A-Z' },
];

const normaliseImageUri = (image) => {
  if (!image || !String(image).trim()) return FALLBACK_IMAGE;

  const trimmed = String(image).trim();

  if (trimmed.startsWith('http://') || trimmed.startsWith('https://')) {
    return trimmed;
  }

  if (trimmed.startsWith('/')) {
    return `https://collabbay.com${trimmed}`;
  }

  return `https://collabbay.com/${trimmed}`;
};

const formatNumber = (value) => {
  const n = Number(value || 0);
  return Number.isFinite(n) ? n.toLocaleString('en-GB') : '0';
};

const formatMoney = (value) => {
  const n = Number(value || 0);

  if (!Number.isFinite(n)) return '£0';

  return `£${n.toLocaleString('en-GB', {
    minimumFractionDigits: n % 1 === 0 ? 0 : 2,
    maximumFractionDigits: 2,
  })}`;
};

const toTimestamp = (value) => {
  if (!value) return 0;

  const date = new Date(value);

  if (Number.isNaN(date.getTime())) return 0;

  return date.getTime();
};

const formatDate = (value) => {
  if (!value) return 'Recently';

  const date = new Date(value);

  if (Number.isNaN(date.getTime())) {
    return String(value);
  }

  return date.toLocaleDateString('en-GB', {
    day: '2-digit',
    month: 'short',
    year: 'numeric',
  });
};

const formatDateTime = (value) => {
  if (!value) return 'Recently';

  const date = new Date(value);

  if (Number.isNaN(date.getTime())) {
    return String(value);
  }

  return date.toLocaleString('en-GB', {
    day: '2-digit',
    month: 'short',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });
};

const formatTime = (value) => {
  if (!value) return '';

  const date = new Date(value);

  if (Number.isNaN(date.getTime())) return '';

  return date.toLocaleTimeString('en-GB', {
    hour: '2-digit',
    minute: '2-digit',
  });
};

const formatDealText = (voucher) => {
  const spend = Number(voucher?.OnSpend || 0);
  const discount = Number(voucher?.Discount || 0);

  if (spend > 0 && discount > 0) {
    return `${discount}% off when spending £${spend}`;
  }

  if (discount > 0) {
    return `${discount}% off`;
  }

  return 'Voucher offer';
};

const getBrandName = (item) => {
  return item?.DisplayBrandName || item?.BrandName || 'Brand';
};

const getCurrentStock = (voucher) => {
  if (voucher?.CurrentStock !== undefined && voucher?.CurrentStock !== null) {
    return Math.max(Number(voucher.CurrentStock || 0), 0);
  }

  if (voucher?.Stock !== undefined && voucher?.Stock !== null) {
    return Math.max(Number(voucher.Stock || 0), 0);
  }

  if (voucher?.Remaining !== undefined && voucher?.Remaining !== null) {
    return Math.max(Number(voucher.Remaining || 0), 0);
  }

  return Math.max(Number(voucher?.StockAssigned || 0), 0);
};

const getPurchaseCount = (voucher) => {
  return Number(voucher?.PurchaseCount || voucher?.Redeemed || 0);
};

const getTotalAmountPaid = (voucher) => {
  return Number(voucher?.TotalAmountPaid || 0);
};

const getPurchaseDate = (purchase) => {
  return purchase?.DateTimePurchased || purchase?.CreatedAt || purchase?.Date;
};

const getVoucherUrl = (voucher) => {
  const id = voucher?.CollabID || '';
  return `${VOUCHER_BASE_URL}${encodeURIComponent(String(id))}`;
};

const getStockKey = (voucher, index) => {
  return `${voucher?.CollabID || 'voucher'}-${voucher?.BrandName || 'brand'}-${index}`;
};

const getPurchaseKey = (purchase, index) => {
  return `${purchase?.VoucherID || 'purchase'}-${purchase?.CollabID || 'collab'}-${index}`;
};

export default function MyVouchers({ navigation }) {
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);

  const [userData, setUserData] = useState({});
  const [vouchers, setVouchers] = useState([]);
  const [purchases, setPurchases] = useState([]);
  const [summary, setSummary] = useState({
    TotalDeals: 0,
    TotalCurrentStock: 0,
    TotalPurchases: 0,
    TotalAmountPaid: 0,
    PotentialCreatorEarnings: 0,
  });

  const [activeTab, setActiveTab] = useState('stock');
  const [stockSort, setStockSort] = useState('latest');
  const [salesSort, setSalesSort] = useState('latest');
  const [expandedStockKey, setExpandedStockKey] = useState(null);
  const [errorMessage, setErrorMessage] = useState('');

  const fetchUserData = async () => {
    try {
      const response = await fetch(SESSION_URL, {
        method: 'GET',
        credentials: 'include',
      });

      const data = await response.json();
      setUserData(data || {});
    } catch (error) {
      console.error('Fetch user data error:', error);
    }
  };

  const fetchCreatorVouchers = async () => {
    const response = await fetch(`${API_URL}?d=${Date.now()}`, {
      method: 'GET',
      credentials: 'include',
      headers: {
        Accept: 'application/json',
      },
    });

    const rawText = await response.text();

    let data;

    try {
      data = JSON.parse(rawText);
    } catch (error) {
      throw new Error(`Invalid voucher response: ${rawText.slice(0, 180)}`);
    }

    if (!response.ok || !data.success) {
      throw new Error(data.message || data.debug || 'Could not load vouchers.');
    }

    setVouchers(Array.isArray(data.vouchers) ? data.vouchers : []);

    setPurchases(
      Array.isArray(data.purchases)
        ? data.purchases
        : Array.isArray(data.ledger)
          ? data.ledger
          : []
    );

    setSummary(
      data.summary || {
        TotalDeals: 0,
        TotalCurrentStock: 0,
        TotalPurchases: 0,
        TotalAmountPaid: 0,
        PotentialCreatorEarnings: 0,
      }
    );

    setErrorMessage('');
  };

  const loadPage = async () => {
    try {
      await Promise.all([fetchUserData(), fetchCreatorVouchers()]);
    } catch (error) {
      console.error('Load creator vouchers error:', error);
      setErrorMessage(error.message || 'Could not load your vouchers.');
    }
  };

  useEffect(() => {
    const run = async () => {
      setLoading(true);
      await loadPage();
      setLoading(false);
    };

    run();
  }, []);

  const onRefresh = async () => {
    setRefreshing(true);
    await loadPage();
    setRefreshing(false);
  };

  const overview = useMemo(() => {
    const currentStock = vouchers.reduce(
      (sum, voucher) => sum + getCurrentStock(voucher),
      0
    );

    const purchaseCount = purchases.length || Number(summary.TotalPurchases || 0);

    const totalSales = purchases.reduce(
      (sum, item) => sum + Number(item.AmountPaid || 0),
      0
    );

    const potential = currentStock * CREATOR_EARNINGS_PER_STOCK;

    const latestPurchase = [...purchases].sort(
      (a, b) => toTimestamp(getPurchaseDate(b)) - toTimestamp(getPurchaseDate(a))
    )[0];

    return {
      currentStock,
      potential,
      purchaseCount,
      totalSales,
      activeBrands: vouchers.length,
      latestPurchaseDate: latestPurchase ? getPurchaseDate(latestPurchase) : null,
    };
  }, [vouchers, purchases, summary.TotalPurchases]);

  const sortedStock = useMemo(() => {
    const list = [...vouchers];

    list.sort((a, b) => {
      const aBrand = getBrandName(a).toLowerCase();
      const bBrand = getBrandName(b).toLowerCase();

      const aStock = getCurrentStock(a);
      const bStock = getCurrentStock(b);

      const aPotential = aStock * CREATOR_EARNINGS_PER_STOCK;
      const bPotential = bStock * CREATOR_EARNINGS_PER_STOCK;

      const aSales = getPurchaseCount(a);
      const bSales = getPurchaseCount(b);

      if (stockSort === 'stock_high') return bStock - aStock;
      if (stockSort === 'potential_high') return bPotential - aPotential;
      if (stockSort === 'sales_high') return bSales - aSales;
      if (stockSort === 'brand_az') return aBrand.localeCompare(bBrand);

      const aDate = toTimestamp(a.LastUpdatedAt || a.FirstAssignedAt);
      const bDate = toTimestamp(b.LastUpdatedAt || b.FirstAssignedAt);

      return bDate - aDate;
    });

    return list;
  }, [vouchers, stockSort]);

  const sortedPurchases = useMemo(() => {
    const list = [...purchases];

    list.sort((a, b) => {
      const aBrand = getBrandName(a).toLowerCase();
      const bBrand = getBrandName(b).toLowerCase();

      const aAmount = Number(a.AmountPaid || 0);
      const bAmount = Number(b.AmountPaid || 0);

      if (salesSort === 'oldest') {
        return toTimestamp(getPurchaseDate(a)) - toTimestamp(getPurchaseDate(b));
      }

      if (salesSort === 'amount_high') {
        return bAmount - aAmount;
      }

      if (salesSort === 'brand_az') {
        return aBrand.localeCompare(bBrand);
      }

      return toTimestamp(getPurchaseDate(b)) - toTimestamp(getPurchaseDate(a));
    });

    return list;
  }, [purchases, salesSort]);

  const handleFooterPress = (routeName, fallbackUrl = null) => {
    if (routeName && navigation?.navigate) {
      navigation.navigate(routeName);
      return;
    }

    if (fallbackUrl) {
      Linking.openURL(fallbackUrl);
    }
  };

  const openVoucherLink = async (voucher) => {
    const url = getVoucherUrl(voucher);

    try {
      await Linking.openURL(url);
    } catch (error) {
      Alert.alert('Voucher link', url);
    }
  };

  const renderHeader = () => (
    <View style={styles.header}>
      <View style={styles.headerLeft}>
        <Image source={{ uri: LOGO_URL }} style={styles.logo} />

        <View>
          <Text style={styles.headerTitle}>Voucher sales</Text>
          <Text style={styles.headerSub}>
            {userData?.Username ? userData.Username : 'Creator dashboard'}
          </Text>
        </View>
      </View>

      <TouchableOpacity
        style={styles.helpButton}
        onPress={() =>
          Alert.alert(
            'Voucher dashboard',
            'Stock shows current live voucher batches. Sales shows actual purchases from voucherpurchases.'
          )
        }
        activeOpacity={0.75}
      >
        <Ionicons name="help-circle-outline" size={21} color="#111" />
      </TouchableOpacity>
    </View>
  );

  const renderOverviewMetric = (label, value, helper = null) => (
    <View style={styles.overviewMetric}>
      <Text style={styles.overviewMetricLabel}>{label}</Text>
      <Text style={styles.overviewMetricValue}>{value}</Text>
      {helper ? <Text style={styles.overviewMetricHelper}>{helper}</Text> : null}
    </View>
  );

  const renderOverview = () => (
    <View style={styles.overviewPanel}>
      <View style={styles.yellowAccentBar} />

      <View style={styles.overviewTop}>
        <View style={{ flex: 1 }}>
          <Text style={styles.overviewLabel}>Voucher overview</Text>
          <Text style={styles.overviewValue}>{formatMoney(overview.totalSales)}</Text>
          <Text style={styles.overviewHelper}>
            Total voucher purchases recorded
            {overview.latestPurchaseDate
              ? ` · latest ${formatDate(overview.latestPurchaseDate)}`
              : ''}
          </Text>
        </View>

        <View style={styles.softBadge}>
          <View style={styles.liveDot} />
          <Text style={styles.softBadgeText}>Live</Text>
        </View>
      </View>

      <View style={styles.overviewLine} />

      <View style={styles.overviewGrid}>
        {renderOverviewMetric('Live stock', formatNumber(overview.currentStock), 'available')}
        {renderOverviewMetric('Potential', formatMoney(overview.potential), 'stock × £5')}
        {renderOverviewMetric('Brands', formatNumber(overview.activeBrands), 'active batches')}
        {renderOverviewMetric('Purchases', formatNumber(overview.purchaseCount), 'orders')}
      </View>
    </View>
  );

  const renderTabs = () => (
    <View style={styles.tabShell}>
      <TouchableOpacity
        style={[styles.tab, activeTab === 'stock' && styles.tabActive]}
        onPress={() => setActiveTab('stock')}
        activeOpacity={0.85}
      >
        <Ionicons
          name="cube-outline"
          size={15}
          color={activeTab === 'stock' ? '#111' : '#777'}
        />
        <Text style={[styles.tabText, activeTab === 'stock' && styles.tabTextActive]}>
          Stock
        </Text>
      </TouchableOpacity>

      <TouchableOpacity
        style={[styles.tab, activeTab === 'sales' && styles.tabActive]}
        onPress={() => setActiveTab('sales')}
        activeOpacity={0.85}
      >
        <Ionicons
          name="receipt-outline"
          size={15}
          color={activeTab === 'sales' ? '#111' : '#777'}
        />
        <Text style={[styles.tabText, activeTab === 'sales' && styles.tabTextActive]}>
          Sales
        </Text>
      </TouchableOpacity>
    </View>
  );

  const renderSortChips = (options, activeValue, onChange) => (
    <ScrollView
      horizontal
      showsHorizontalScrollIndicator={false}
      contentContainerStyle={styles.sortRow}
    >
      {options.map((option) => {
        const active = activeValue === option.key;

        return (
          <TouchableOpacity
            key={option.key}
            style={[styles.sortChip, active && styles.sortChipActive]}
            onPress={() => onChange(option.key)}
            activeOpacity={0.78}
          >
            <Text style={[styles.sortChipText, active && styles.sortChipTextActive]}>
              {option.label}
            </Text>
          </TouchableOpacity>
        );
      })}
    </ScrollView>
  );

  const renderEmptyState = (icon, title, body) => (
    <View style={styles.emptyPanel}>
      <View style={styles.emptyIcon}>
        <Ionicons name={icon} size={24} color="#555" />
      </View>
      <Text style={styles.emptyTitle}>{title}</Text>
      <Text style={styles.emptyBody}>{body}</Text>
    </View>
  );

  const renderSectionIntro = (title, subtitle) => (
    <View style={styles.sectionIntro}>
      <Text style={styles.sectionTitle}>{title}</Text>
      <Text style={styles.sectionSubtitle}>{subtitle}</Text>
    </View>
  );

  const renderMiniPurchaseRow = (purchase, index) => (
    <View key={getPurchaseKey(purchase, index)} style={styles.miniPurchaseRow}>
      <View style={{ flex: 1 }}>
        <Text style={styles.miniPurchaseTitle} numberOfLines={1}>
          {purchase.CustomerEmail || 'Customer'}
        </Text>
        <Text style={styles.miniPurchaseMeta} numberOfLines={1}>
          {formatDateTime(getPurchaseDate(purchase))}
        </Text>
      </View>

      <Text style={styles.miniPurchaseAmount}>
        {formatMoney(purchase.AmountPaid)}
      </Text>
    </View>
  );

  const renderStockRow = (voucher, index) => {
    const key = getStockKey(voucher, index);
    const expanded = expandedStockKey === key;

    const brandImage = normaliseImageUri(voucher.BrandProfileImage);
    const brandName = getBrandName(voucher);
    const currentStock = getCurrentStock(voucher);
    const purchaseCount = getPurchaseCount(voucher);
    const amountSold = getTotalAmountPaid(voucher);
    const potential = currentStock * CREATOR_EARNINGS_PER_STOCK;
    const latestDate = voucher.LastPurchasedAt || voucher.LastUpdatedAt || voucher.FirstAssignedAt;

    const brandPurchases = purchases
      .filter((purchase) => String(purchase.CollabID) === String(voucher.CollabID))
      .sort((a, b) => toTimestamp(getPurchaseDate(b)) - toTimestamp(getPurchaseDate(a)))
      .slice(0, 3);

    return (
      <View key={key} style={styles.stockRowWrap}>
        <TouchableOpacity
          style={styles.stockRow}
          activeOpacity={0.82}
          onPress={() => setExpandedStockKey(expanded ? null : key)}
        >
          <Image source={{ uri: brandImage }} style={styles.brandImage} />

          <View style={styles.stockBrandArea}>
            <Text style={styles.brandName} numberOfLines={1}>
              {brandName}
            </Text>

            <Text style={styles.brandDescription} numberOfLines={1}>
              {formatDealText(voucher)}
            </Text>

            <Text style={styles.rowMutedText} numberOfLines={1}>
              Updated {formatDate(latestDate)}
            </Text>
          </View>

          <View style={styles.stockValueArea}>
            <Text style={styles.rowPrimaryValue}>{formatNumber(currentStock)}</Text>
            <Text style={styles.rowMutedText}>left</Text>
          </View>

          <Ionicons
            name={expanded ? 'chevron-up' : 'chevron-down'}
            size={17}
            color="#888"
            style={styles.rowChevron}
          />
        </TouchableOpacity>

        {expanded ? (
          <View style={styles.expandedPanel}>
            <View style={styles.expandedGrid}>
              <View style={styles.cleanMetric}>
                <Text style={styles.cleanMetricLabel}>Current stock</Text>
                <Text style={styles.cleanMetricValue}>{formatNumber(currentStock)}</Text>
              </View>

              <View style={styles.cleanMetric}>
                <Text style={styles.cleanMetricLabel}>Potential</Text>
                <Text style={styles.cleanMetricValue}>{formatMoney(potential)}</Text>
              </View>

              <View style={styles.cleanMetric}>
                <Text style={styles.cleanMetricLabel}>Purchases</Text>
                <Text style={styles.cleanMetricValue}>{formatNumber(purchaseCount)}</Text>
              </View>

              <View style={styles.cleanMetric}>
                <Text style={styles.cleanMetricLabel}>Sold</Text>
                <Text style={styles.cleanMetricValue}>{formatMoney(amountSold)}</Text>
              </View>
            </View>

            <View style={styles.detailRows}>
              <View style={styles.detailRow}>
                <Text style={styles.detailLabel}>Voucher page</Text>
                <Text style={styles.detailValue} numberOfLines={1}>
                  {getVoucherUrl(voucher).replace('https://', '')}
                </Text>
              </View>

              <View style={styles.detailRow}>
                <Text style={styles.detailLabel}>Created</Text>
                <Text style={styles.detailValue}>
                  {formatDate(voucher.FirstAssignedAt)}
                </Text>
              </View>

              <View style={styles.detailRow}>
                <Text style={styles.detailLabel}>Last update</Text>
                <Text style={styles.detailValue}>
                  {formatDateTime(voucher.LastUpdatedAt)}
                </Text>
              </View>
            </View>

            {brandPurchases.length > 0 ? (
              <View style={styles.recentPurchasesBox}>
                <Text style={styles.recentPurchasesTitle}>Recent purchases</Text>
                {brandPurchases.map(renderMiniPurchaseRow)}
              </View>
            ) : (
              <View style={styles.noRecentPurchasesBox}>
                <Text style={styles.noRecentPurchasesText}>
                  No purchases for this voucher yet.
                </Text>
              </View>
            )}

            <TouchableOpacity
              style={styles.linkButton}
              onPress={() => openVoucherLink(voucher)}
              activeOpacity={0.82}
            >
              <Text style={styles.linkButtonText}>View voucher sales link</Text>
              <Ionicons name="open-outline" size={16} color="#111" />
            </TouchableOpacity>
          </View>
        ) : null}
      </View>
    );
  };

  const renderStockTab = () => (
    <View>
      {renderSectionIntro(
        'Live stock',
        'Tap a brand to expand voucher details, view recent purchases and open its sales page.'
      )}

      {renderSortChips(STOCK_SORT_OPTIONS, stockSort, setStockSort)}

      {sortedStock.length === 0 ? (
        renderEmptyState(
          'ticket-outline',
          'No voucher stock yet',
          'When a venue assigns vouchers to you, they will appear here.'
        )
      ) : (
        <View style={styles.dataPanel}>
          <View style={styles.stockHeaderRow}>
            <Text style={[styles.tableHeaderText, { flex: 1 }]}>Brand</Text>
            <Text style={styles.tableHeaderText}>Stock</Text>
          </View>

          {sortedStock.map((voucher, index) => renderStockRow(voucher, index))}
        </View>
      )}
    </View>
  );

  const renderPurchaseRow = (purchase, index) => {
    const brandImage = normaliseImageUri(purchase.BrandProfileImage);
    const brandName = getBrandName(purchase);
    const date = getPurchaseDate(purchase);

    return (
      <View key={getPurchaseKey(purchase, index)} style={styles.purchaseRow}>
        <View style={styles.purchaseDateArea}>
          <Text style={styles.purchaseDate}>{formatDate(date)}</Text>
          <Text style={styles.purchaseTime}>{formatTime(date)}</Text>
        </View>

        <Image source={{ uri: brandImage }} style={styles.purchaseImage} />

        <View style={styles.purchaseMain}>
          <Text style={styles.purchaseBrand} numberOfLines={1}>
            {brandName}
          </Text>

          <Text style={styles.purchaseCustomer} numberOfLines={1}>
            {purchase.CustomerEmail || 'Customer email unavailable'}
          </Text>

          <Text style={styles.purchaseReference} numberOfLines={1}>
            Passcode {purchase.VoucherPasscode || '—'} · Voucher #{purchase.VoucherID || '—'}
          </Text>
        </View>

        <View style={styles.purchaseAmountArea}>
          <Text style={styles.purchaseAmount}>
            {formatMoney(purchase.AmountPaid)}
          </Text>

          <View style={styles.paidPill}>
            <View style={styles.paidDot} />
            <Text style={styles.paidText}>Paid</Text>
          </View>
        </View>
      </View>
    );
  };

  const renderSalesTab = () => (
    <View>
      {renderSectionIntro(
        'Sales ledger',
        'Actual customer purchases from voucherpurchases, with dates, passcodes and amounts.'
      )}

      {renderSortChips(SALES_SORT_OPTIONS, salesSort, setSalesSort)}

      {sortedPurchases.length === 0 ? (
        renderEmptyState(
          'receipt-outline',
          'No voucher purchases yet',
          'When customers purchase vouchers through your sales links, they will appear here.'
        )
      ) : (
        <View style={styles.dataPanel}>
          <View style={styles.salesHeaderRow}>
            <Text style={[styles.tableHeaderText, { width: 82 }]}>Date</Text>
            <Text style={[styles.tableHeaderText, { flex: 1 }]}>Purchase</Text>
            <Text style={styles.tableHeaderText}>Amount</Text>
          </View>

          {sortedPurchases.map((purchase, index) => renderPurchaseRow(purchase, index))}
        </View>
      )}
    </View>
  );

  if (loading) {
    return (
      <View style={styles.loadingPage}>
        <ActivityIndicator size="large" color="#111" />
        <Text style={styles.loadingText}>Loading voucher dashboard...</Text>
      </View>
    );
  }

  return (
    <View style={styles.outerPage}>
      <View style={styles.yellowStrip} />

      <SafeAreaView style={styles.container}>
        <View style={styles.backgroundGlow} pointerEvents="none" />
        <View style={styles.backgroundCircleOne} pointerEvents="none" />
        <View style={styles.backgroundCircleTwo} pointerEvents="none" />

        <ScrollView
          contentContainerStyle={styles.scrollContent}
          showsVerticalScrollIndicator={false}
          refreshControl={
            <RefreshControl refreshing={refreshing} onRefresh={onRefresh} />
          }
        >
          {renderHeader()}

          {errorMessage ? (
            <View style={styles.errorPanel}>
              <Text style={styles.errorTitle}>Could not load vouchers</Text>
              <Text style={styles.errorText}>{errorMessage}</Text>
            </View>
          ) : null}

          {renderOverview()}
          {renderTabs()}

          {activeTab === 'stock' ? renderStockTab() : renderSalesTab()}

          <TouchableOpacity
            style={styles.privacyLink}
            onPress={() =>
              Linking.openURL('https://collabbay.com/PrivacyPolicy.html')
            }
          >
            <Text style={styles.privacyText}>
              By using collabbay, you agree to our Privacy Policy.
            </Text>
          </TouchableOpacity>
        </ScrollView>

        <View style={styles.footer}>
          <TouchableOpacity
            style={styles.footerIcon}
            onPress={() => handleFooterPress('Homepage')}
          >
            <Ionicons name="home" size={24} color="black" />
          </TouchableOpacity>

          <TouchableOpacity
            style={styles.footerIcon}
            onPress={() => handleFooterPress('CollabCalendar')}
          >
            <Ionicons name="calendar" size={24} color="black" />
          </TouchableOpacity>

          <TouchableOpacity
            onPress={() => handleFooterPress('MyFeed')}
            style={styles.footerMainButton}
          >
            <Ionicons name="film-outline" size={22} color="black" />
          </TouchableOpacity>

          <TouchableOpacity
            style={styles.footerIcon}
            onPress={() => handleFooterPress('UserActivityFeed')}
          >
            <Ionicons name="chatbox-ellipses-outline" size={24} color="black" />
          </TouchableOpacity>

          <TouchableOpacity
            style={styles.footerIcon}
            onPress={() => handleFooterPress('UserRecapPage')}
          >
            <Ionicons name="person" size={24} color="black" />
          </TouchableOpacity>
        </View>
      </SafeAreaView>
    </View>
  );
}

const styles = StyleSheet.create({
  outerPage: {
    flex: 1,
    backgroundColor: '#ffffff',
    alignItems: 'center',
  },
  yellowStrip: {
    height: 12,
    width: '100%',
    maxWidth: 600,
    backgroundColor: '#FDF05D',
  },
  container: {
    flex: 1,
    width: '100%',
    maxWidth: 600,
    backgroundColor: '#ffffff',
    position: 'relative',
    overflow: 'hidden',
  },
  backgroundGlow: {
    position: 'absolute',
    top: 0,
    left: 0,
    right: 0,
    height: 220,
    backgroundColor: 'rgba(253,240,93,0.22)',
  },
  backgroundCircleOne: {
    position: 'absolute',
    top: 80,
    right: -70,
    width: 170,
    height: 170,
    borderRadius: 85,
    backgroundColor: 'rgba(253,240,93,0.16)',
  },
  backgroundCircleTwo: {
    position: 'absolute',
    top: 280,
    left: -80,
    width: 170,
    height: 170,
    borderRadius: 85,
    backgroundColor: 'rgba(0,0,0,0.025)',
  },
  scrollContent: {
    paddingHorizontal: 14,
    paddingBottom: 110,
  },

  loadingPage: {
    flex: 1,
    backgroundColor: '#ffffff',
    alignItems: 'center',
    justifyContent: 'center',
  },
  loadingText: {
    marginTop: 10,
    color: '#555',
    fontSize: 13,
    fontWeight: '500',
  },

  header: {
    paddingTop: 20,
    paddingBottom: 14,
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    zIndex: 2,
  },
  headerLeft: {
    flexDirection: 'row',
    alignItems: 'center',
    flex: 1,
  },
  logo: {
    width: 46,
    height: 46,
    borderRadius: 23,
    backgroundColor: '#ffffff',
    borderWidth: 1,
    borderColor: '#ececec',
    marginRight: 12,
  },
  headerTitle: {
    fontSize: 24,
    fontWeight: '600',
    color: '#111111',
    letterSpacing: -0.5,
  },
  headerSub: {
    marginTop: 3,
    fontSize: 13,
    color: '#666666',
    fontWeight: '500',
  },
  helpButton: {
    width: 39,
    height: 39,
    borderRadius: 19.5,
    backgroundColor: '#ffffff',
    borderWidth: 1,
    borderColor: '#eeeeee',
    alignItems: 'center',
    justifyContent: 'center',
  },

  errorPanel: {
    backgroundColor: '#fff7f7',
    borderWidth: 1,
    borderColor: '#ffd0d0',
    borderRadius: 18,
    padding: 14,
    marginBottom: 14,
  },
  errorTitle: {
    color: '#9f1d1d',
    fontSize: 14,
    fontWeight: '600',
  },
  errorText: {
    color: '#9f1d1d',
    fontSize: 12,
    marginTop: 4,
    lineHeight: 17,
    fontWeight: '500',
  },

  overviewPanel: {
    backgroundColor: 'rgba(255,255,255,0.96)',
    borderRadius: 28,
    padding: 18,
    borderWidth: 1,
    borderColor: '#eeeeee',
    marginBottom: 14,
    shadowColor: '#000',
    shadowOpacity: 0.055,
    shadowRadius: 18,
    shadowOffset: { width: 0, height: 8 },
    elevation: 2,
    overflow: 'hidden',
  },
  yellowAccentBar: {
    position: 'absolute',
    top: 0,
    left: 0,
    right: 0,
    height: 5,
    backgroundColor: '#FDF05D',
  },
  overviewTop: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'flex-start',
    gap: 12,
  },
  overviewLabel: {
    fontSize: 12,
    color: '#777777',
    fontWeight: '600',
    marginBottom: 6,
    letterSpacing: 0.2,
  },
  overviewValue: {
    fontSize: 34,
    color: '#111111',
    fontWeight: '600',
    letterSpacing: -1.2,
  },
  overviewHelper: {
    marginTop: 6,
    fontSize: 13,
    color: '#666666',
    lineHeight: 19,
    fontWeight: '500',
  },
  softBadge: {
    borderRadius: 999,
    paddingVertical: 7,
    paddingHorizontal: 10,
    backgroundColor: '#f8f8f8',
    borderWidth: 1,
    borderColor: '#eeeeee',
    flexDirection: 'row',
    alignItems: 'center',
    gap: 6,
  },
  liveDot: {
    width: 7,
    height: 7,
    borderRadius: 3.5,
    backgroundColor: '#21a453',
  },
  softBadgeText: {
    color: '#222222',
    fontSize: 12,
    fontWeight: '600',
  },
  overviewLine: {
    height: 1,
    backgroundColor: '#eeeeee',
    marginVertical: 16,
  },
  overviewGrid: {
    flexDirection: 'row',
    flexWrap: 'wrap',
  },
  overviewMetric: {
    width: '50%',
    paddingVertical: 8,
    paddingRight: 12,
  },
  overviewMetricLabel: {
    fontSize: 12,
    color: '#777777',
    fontWeight: '500',
    marginBottom: 5,
  },
  overviewMetricValue: {
    fontSize: 18,
    color: '#111111',
    fontWeight: '600',
  },
  overviewMetricHelper: {
    marginTop: 3,
    fontSize: 11,
    color: '#999999',
    fontWeight: '500',
  },

  tabShell: {
    flexDirection: 'row',
    backgroundColor: '#f1f1f1',
    borderRadius: 999,
    padding: 5,
    marginBottom: 18,
    borderWidth: 1,
    borderColor: '#eeeeee',
  },
  tab: {
    flex: 1,
    borderRadius: 999,
    paddingVertical: 10,
    alignItems: 'center',
    justifyContent: 'center',
    flexDirection: 'row',
    gap: 6,
  },
  tabActive: {
    backgroundColor: '#ffffff',
    shadowColor: '#000',
    shadowOpacity: 0.055,
    shadowRadius: 8,
    shadowOffset: { width: 0, height: 3 },
    elevation: 1,
  },
  tabText: {
    fontSize: 13,
    color: '#777777',
    fontWeight: '600',
  },
  tabTextActive: {
    color: '#111111',
  },

  sectionIntro: {
    marginBottom: 10,
  },
  sectionTitle: {
    fontSize: 20,
    color: '#111111',
    fontWeight: '600',
    letterSpacing: -0.4,
  },
  sectionSubtitle: {
    marginTop: 5,
    fontSize: 13,
    color: '#666666',
    lineHeight: 19,
    fontWeight: '500',
  },

  sortRow: {
    paddingBottom: 13,
    gap: 8,
  },
  sortChip: {
    backgroundColor: '#ffffff',
    borderWidth: 1,
    borderColor: '#eeeeee',
    borderRadius: 999,
    paddingVertical: 8,
    paddingHorizontal: 12,
  },
  sortChipActive: {
    backgroundColor: '#fffde0',
    borderColor: '#FDF05D',
  },
  sortChipText: {
    color: '#666666',
    fontSize: 12,
    fontWeight: '500',
  },
  sortChipTextActive: {
    color: '#111111',
    fontWeight: '600',
  },

  dataPanel: {
    backgroundColor: '#ffffff',
    borderRadius: 24,
    borderWidth: 1,
    borderColor: '#eeeeee',
    overflow: 'hidden',
    shadowColor: '#000',
    shadowOpacity: 0.045,
    shadowRadius: 14,
    shadowOffset: { width: 0, height: 5 },
    elevation: 1,
  },
  stockHeaderRow: {
    paddingHorizontal: 16,
    paddingVertical: 12,
    backgroundColor: '#fafafa',
    borderBottomWidth: 1,
    borderBottomColor: '#eeeeee',
    flexDirection: 'row',
    alignItems: 'center',
  },
  salesHeaderRow: {
    paddingHorizontal: 16,
    paddingVertical: 12,
    backgroundColor: '#fafafa',
    borderBottomWidth: 1,
    borderBottomColor: '#eeeeee',
    flexDirection: 'row',
    alignItems: 'center',
  },
  tableHeaderText: {
    fontSize: 11,
    color: '#888888',
    fontWeight: '600',
    textTransform: 'uppercase',
    letterSpacing: 0.5,
  },

  stockRowWrap: {
    borderBottomWidth: 1,
    borderBottomColor: '#eeeeee',
  },
  stockRow: {
    flexDirection: 'row',
    alignItems: 'center',
    padding: 16,
  },
  brandImage: {
    width: 76,
    height: 76,
    borderRadius: 38,
    backgroundColor: '#f1f1f1',
    borderWidth: 1,
    borderColor: '#eeeeee',
    marginRight: 15,
  },
  stockBrandArea: {
    flex: 1,
    minWidth: 0,
  },
  brandName: {
    fontSize: 16,
    color: '#111111',
    fontWeight: '600',
    letterSpacing: -0.1,
  },
  brandDescription: {
    marginTop: 5,
    fontSize: 13,
    color: '#666666',
    fontWeight: '500',
    lineHeight: 18,
  },
  rowMutedText: {
    marginTop: 4,
    fontSize: 12,
    color: '#999999',
    fontWeight: '500',
  },
  stockValueArea: {
    alignItems: 'flex-end',
    minWidth: 44,
    marginLeft: 10,
  },
  rowPrimaryValue: {
    fontSize: 18,
    color: '#111111',
    fontWeight: '600',
  },
  rowChevron: {
    marginLeft: 8,
  },

  expandedPanel: {
    paddingHorizontal: 16,
    paddingBottom: 16,
  },
  expandedGrid: {
    flexDirection: 'row',
    flexWrap: 'wrap',
    gap: 8,
    marginBottom: 12,
  },
  cleanMetric: {
    width: '48.5%',
    backgroundColor: '#fbfbfb',
    borderRadius: 16,
    padding: 12,
    borderWidth: 1,
    borderColor: '#eeeeee',
  },
  cleanMetricLabel: {
    fontSize: 12,
    color: '#777777',
    fontWeight: '500',
    marginBottom: 5,
  },
  cleanMetricValue: {
    fontSize: 17,
    color: '#111111',
    fontWeight: '600',
  },
  detailRows: {
    borderTopWidth: 1,
    borderTopColor: '#eeeeee',
  },
  detailRow: {
    flexDirection: 'row',
    alignItems: 'center',
    paddingVertical: 9,
    borderBottomWidth: 1,
    borderBottomColor: '#eeeeee',
  },
  detailLabel: {
    width: 98,
    fontSize: 12,
    color: '#888888',
    fontWeight: '500',
  },
  detailValue: {
    flex: 1,
    fontSize: 12,
    color: '#222222',
    fontWeight: '500',
  },
  recentPurchasesBox: {
    marginTop: 12,
    borderRadius: 16,
    backgroundColor: '#fffef1',
    borderWidth: 1,
    borderColor: '#f5ed91',
    overflow: 'hidden',
  },
  recentPurchasesTitle: {
    paddingHorizontal: 12,
    paddingTop: 11,
    paddingBottom: 8,
    fontSize: 12,
    color: '#444444',
    fontWeight: '600',
  },
  miniPurchaseRow: {
    paddingHorizontal: 12,
    paddingVertical: 10,
    borderTopWidth: 1,
    borderTopColor: 'rgba(0,0,0,0.055)',
    flexDirection: 'row',
    alignItems: 'center',
  },
  miniPurchaseTitle: {
    fontSize: 12,
    color: '#222222',
    fontWeight: '600',
  },
  miniPurchaseMeta: {
    marginTop: 3,
    fontSize: 11,
    color: '#777777',
    fontWeight: '500',
  },
  miniPurchaseAmount: {
    fontSize: 13,
    color: '#111111',
    fontWeight: '600',
    marginLeft: 10,
  },
  noRecentPurchasesBox: {
    marginTop: 12,
    borderRadius: 16,
    backgroundColor: '#fafafa',
    borderWidth: 1,
    borderColor: '#eeeeee',
    padding: 12,
  },
  noRecentPurchasesText: {
    color: '#777777',
    fontSize: 12,
    fontWeight: '500',
  },
  linkButton: {
    marginTop: 13,
    borderRadius: 999,
    paddingVertical: 12,
    paddingHorizontal: 14,
    backgroundColor: '#FDF05D',
    borderWidth: 1,
    borderColor: '#eee25a',
    alignItems: 'center',
    justifyContent: 'center',
    flexDirection: 'row',
    gap: 8,
  },
  linkButtonText: {
    color: '#111111',
    fontSize: 14,
    fontWeight: '600',
  },

  purchaseRow: {
    flexDirection: 'row',
    alignItems: 'center',
    paddingHorizontal: 16,
    paddingVertical: 14,
    borderBottomWidth: 1,
    borderBottomColor: '#eeeeee',
  },
  purchaseDateArea: {
    width: 82,
  },
  purchaseDate: {
    fontSize: 12,
    color: '#222222',
    fontWeight: '600',
  },
  purchaseTime: {
    marginTop: 3,
    fontSize: 11,
    color: '#999999',
    fontWeight: '500',
  },
  purchaseImage: {
    width: 50,
    height: 50,
    borderRadius: 25,
    backgroundColor: '#f1f1f1',
    borderWidth: 1,
    borderColor: '#eeeeee',
    marginRight: 12,
  },
  purchaseMain: {
    flex: 1,
    minWidth: 0,
  },
  purchaseBrand: {
    fontSize: 14,
    color: '#111111',
    fontWeight: '600',
  },
  purchaseCustomer: {
    marginTop: 3,
    fontSize: 12,
    color: '#666666',
    fontWeight: '500',
  },
  purchaseReference: {
    marginTop: 3,
    fontSize: 11,
    color: '#999999',
    fontWeight: '500',
  },
  purchaseAmountArea: {
    alignItems: 'flex-end',
    marginLeft: 8,
  },
  purchaseAmount: {
    fontSize: 15,
    color: '#111111',
    fontWeight: '600',
  },
  paidPill: {
    marginTop: 5,
    borderRadius: 999,
    paddingVertical: 4,
    paddingHorizontal: 7,
    backgroundColor: '#eaf8ef',
    flexDirection: 'row',
    alignItems: 'center',
    gap: 5,
  },
  paidDot: {
    width: 6,
    height: 6,
    borderRadius: 3,
    backgroundColor: '#21a453',
  },
  paidText: {
    fontSize: 10,
    color: '#16833a',
    fontWeight: '600',
  },

  emptyPanel: {
    backgroundColor: '#ffffff',
    borderRadius: 24,
    borderWidth: 1,
    borderColor: '#eeeeee',
    padding: 28,
    alignItems: 'center',
  },
  emptyIcon: {
    width: 52,
    height: 52,
    borderRadius: 26,
    backgroundColor: '#fffde0',
    borderWidth: 1,
    borderColor: '#FDF05D',
    alignItems: 'center',
    justifyContent: 'center',
  },
  emptyTitle: {
    marginTop: 12,
    fontSize: 17,
    color: '#111111',
    fontWeight: '600',
    textAlign: 'center',
  },
  emptyBody: {
    marginTop: 6,
    fontSize: 13,
    color: '#666666',
    fontWeight: '500',
    lineHeight: 19,
    textAlign: 'center',
  },

  privacyLink: {
    marginTop: 24,
    marginBottom: 8,
  },
  privacyText: {
    fontSize: 11,
    color: '#111111',
    textDecorationLine: 'underline',
    textAlign: 'center',
    fontWeight: '500',
  },

  footer: {
    flexDirection: 'row',
    justifyContent: 'space-around',
    position: 'sticky',
    bottom: 0,
    backgroundColor: 'white',
    left: 0,
    right: 0,
    paddingVertical: 10,
    borderTopWidth: 1,
    borderTopColor: '#f0f0f0',
  },
  footerIcon: {
    alignItems: 'center',
    justifyContent: 'center',
    width: 42,
    height: 42,
  },
  footerMainButton: {
    backgroundColor: '#FDF05D',
    padding: 10,
    borderRadius: 20,
  },
});